import type { CSSProperties, ReactNode, PointerEvent as ReactPointerEvent } from 'react'
import { cn } from './cn.js'

export type ToastTone = 'utility' | 'persona' | 'danger'

export interface ToastChip {
  label: string
  onSelect: () => void
}

export interface ToastProps {
  message: ReactNode
  actionLabel?: string
  onAction?: () => void
  /**
   * Reply chips under the message — the persona toasts' talk-back affordance.
   * Mutually exclusive with `actionLabel` by contract (the store enforces it):
   * an undo offer is never crowded by banter.
   */
  chips?: ReadonlyArray<ToastChip>
  /**
   * Who is talking. `utility` is the app confirming something and keeps the
   * lime outline it has always had; `persona` is her, in her own colour and a
   * size up; `danger` is a failure, which used to be indistinguishable from a
   * logged breakfast.
   */
  tone?: ToastTone
  /** Her mark, for the toasts that are her speaking. */
  leading?: ReactNode
  /**
   * Announce this one assertively. Used for failures, which the host's polite
   * region would otherwise read out behind whatever is already queued — and a
   * failure heard after the next two confirmations is a failure nobody heard.
   */
  alert?: boolean
  /** Playing its exit animation. */
  leaving?: boolean
  /** Held open by a tap. Shown, or the hold is a mystery rather than a feature. */
  held?: boolean
  /** Live horizontal drag offset, in px, while a swipe is in progress. */
  dragX?: number
  onPointerDown?: (event: ReactPointerEvent<HTMLDivElement>) => void
  onPointerMove?: (event: ReactPointerEvent<HTMLDivElement>) => void
  onPointerUp?: (event: ReactPointerEvent<HTMLDivElement>) => void
  onPointerCancel?: (event: ReactPointerEvent<HTMLDivElement>) => void
}

/**
 * One line of feedback with at most one way to act on it.
 *
 * The outline and its halo are the one place this design spends a glow: a
 * toast arrives while the eye is on the row that was just touched, and a
 * hairline down at the bottom edge of a dense screen was being missed
 * outright. Which colour that glow is answers a second question the design did
 * not used to answer — whether this is the app confirming a write or the
 * narrator having an opinion about it. Lime is the instrument. Violet is her.
 *
 * Three lines of clamp, not two: the voice catalogue's toast budget is 120
 * characters, which is a spoken sentence rather than a telegram, and at that
 * width a narrow phone needs the third line. Past three it is a paragraph in
 * the wrong place, and the clamp is doing its job.
 */
export function Toast({
  message,
  actionLabel,
  onAction,
  chips,
  tone = 'utility',
  leading,
  alert,
  leaving,
  held,
  dragX,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: ToastProps) {
  const persona = tone === 'persona'
  const danger = tone === 'danger'

  return (
    <div
      role={alert === true ? 'alert' : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      style={
        dragX != null && dragX !== 0
          ? ({
              transform: `translate3d(${dragX}px,0,0)`,
              opacity: swipeFade(dragX),
            } as CSSProperties)
          : undefined
      }
      className={cn(
        'pointer-events-auto flex touch-pan-y select-none items-center gap-2 rounded-[var(--radius-card)] bg-raised',
        // Entry and exit both read the tone's own timing, so a Wednesday toast
        // arrives with its mind made up and a November one takes its time.
        leaving === true
          ? 'animate-[toast-out_180ms_var(--ease-out-quick)_both]'
          : 'animate-[toast-in_var(--toast-enter-ms,160ms)_var(--toast-enter-ease)_both]',
        // Surface, ring and lift together. `raised` alone was a white card on a
        // white page in light theme — the design's own elevation token, doing
        // no elevating. Each toast now carries a wash of the colour that says
        // whose it is, and a shadow that says it is lying on top of the day
        // rather than being part of it.
        //
        // The wash is a background *layer* over the opaque surface above, not a
        // colour mixed into it. A tint is an enhancement and has to fail like
        // one: an undefined or unsupported colour in `background-color` does not
        // leave the old colour behind, it leaves nothing, and a toast you can
        // read the diary through is worse than a toast with no tint.
        persona
          ? 'border border-[var(--her)]/70 bg-[linear-gradient(var(--her-wash),var(--her-wash))] py-3 pl-3.5 pr-2 shadow-[0_0_0_3px_var(--her-glow),var(--g-toast-lift)]'
          : danger
            ? 'border border-danger/70 bg-[linear-gradient(var(--g-danger-wash),var(--g-danger-wash))] py-2 pl-4 pr-2 shadow-[0_0_0_3px_var(--color-danger-glow),var(--g-toast-lift)]'
            : 'border border-accent/70 bg-[linear-gradient(var(--g-accent-wash),var(--g-accent-wash))] py-2 pl-4 pr-2 shadow-[0_0_0_3px_var(--color-accent-glow),var(--g-toast-lift)]',
      )}
    >
      {leading}
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span
          className={cn(
            'line-clamp-3 min-w-0',
            // A size up for her. She is not confirming a write; she is talking,
            // and at 13px in a corner that reads as a system message.
            persona ? 'text-[0.9375rem] leading-[1.375rem] text-ink' : 'text-sm text-ink',
          )}
        >
          {message}
        </span>
        {chips != null && chips.length > 0 && (
          <span className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onClick={chip.onSelect}
                className={cn(
                  'shrink-0 rounded-full border px-2.5 py-1 font-medium text-xs active:bg-surface',
                  persona ? 'border-[var(--her)]/50 text-[var(--her)]' : 'border-line text-dim',
                )}
              >
                {chip.label}
              </button>
            ))}
          </span>
        )}
      </span>
      {held === true && (
        // Small enough to be missed, which is right: it explains the hold to
        // whoever went looking for an explanation, and stays out of the way of
        // whoever did not.
        <span
          aria-hidden="true"
          className={cn(
            'mr-0.5 h-3.5 w-[3px] shrink-0 rounded-full',
            persona ? 'bg-[var(--her)]' : 'bg-mute',
          )}
        />
      )}
      {actionLabel != null && onAction != null && (
        <button
          type="button"
          onClick={onAction}
          className={cn(
            "relative shrink-0 rounded-[var(--radius-control)] px-2.5 py-1.5 font-semibold text-sm before:absolute before:-inset-1.5 before:content-[''] active:bg-surface",
            persona ? 'text-[var(--her)]' : danger ? 'text-danger' : 'text-accent-text',
          )}
        >
          {actionLabel}
        </button>
      )}
    </div>
  )
}

/** A swipe fades as it travels, so letting go halfway looks like a decision. */
function swipeFade(dragX: number): number {
  return Math.max(0.25, 1 - Math.abs(dragX) / 220)
}
